import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { callFindId, callSendEmailCode, callResetPasswordEmail } from "api/MuyeonApi";
import TextInput from "components/ui/TextInput";
import EmailField from "components/muyeon/EmailField";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * 아이디(가입 이메일)/비밀번호 찾기 — 후니드 AccountRecovery DOM 구조 재사용.
 *  - ?type=id : 이름+휴대폰 → 가입 이메일
 *  - ?type=pw : 이메일 인증(코드) → 새 비밀번호 설정
 * 패딩 출처: #find_account .content-wrap .form-list{padding:4rem 2rem}
 */
const AccountRecovery = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const type = params.get("type") === "pw" ? "pw" : "id";

  const [f, setF] = useState({
    name: "", email: "", phone: "", code: "", newPassword: "", newPasswordConfirm: "",
  });
  const [resultMsg, setResultMsg] = useState("");
  const [showModal, setShowModal] = useState(false);

  // 비번재설정: 이메일 인증 상태
  const [codeSent, setCodeSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [vmsg, setVmsg] = useState("");
  const [vmsgErr, setVmsgErr] = useState(false); // true=에러(빨강)
  const showErr = (m) => { setVmsg(m); setVmsgErr(true); };
  const showInfo = (m) => { setVmsg(m); setVmsgErr(false); };

  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onEmail = (e) => {
    setF({ ...f, email: e.target.value });
    setCodeSent(false);
    setVmsg("");
  };

  const sendCode = async () => {
    if (!EMAIL_RE.test(f.email)) {
      showErr("올바른 이메일 주소를 입력해 주세요.");
      return;
    }
    setSending(true);
    setVmsg("");
    try {
      await callSendEmailCode({ email: f.email, purpose: "RESET" });
      setCodeSent(true);
      showInfo("인증번호를 보냈어요. 메일함과 스팸함을 확인해 주세요. 인증번호는 10분 동안 쓸 수 있어요.");
    } catch (e) {
      showErr(e?.response?.data?.message || "인증번호 발송에 실패했어요.");
    } finally {
      setSending(false);
    }
  };

  const disabled =
    type === "id"
      ? !(f.name && f.phone)
      : !(f.email && f.code && f.newPassword && f.newPasswordConfirm);

  const submit = async () => {
    if (type === "id") {
      try {
        const res = await callFindId({ name: f.name, phone: f.phone });
        setResultMsg(`가입한 이메일은\n${res.data.email}\n이에요.`);
      } catch (e) {
        setResultMsg(e?.response?.data?.message || "일치하는 회원 정보가 없어요.");
      }
      setShowModal(true);
      return;
    }
    // 비밀번호 재설정
    if (!f.code || f.code.length < 6) {
      showErr("인증번호 6자리를 입력해 주세요.");
      return;
    }
    if (f.newPassword.length < 8) {
      showErr("비밀번호는 8자 이상이어야 해요.");
      return;
    }
    if (f.newPassword !== f.newPasswordConfirm) {
      showErr("비밀번호가 일치하지 않아요.");
      return;
    }
    try {
      setVmsg("");
      await callResetPasswordEmail({ email: f.email, code: f.code, newPassword: f.newPassword });
      setResultMsg("비밀번호가 변경됐어요.\n새 비밀번호로 로그인해 주세요.");
      setShowModal(true);
    } catch (e) {
      showErr(e?.response?.data?.message || "비밀번호 재설정에 실패했어요.");
    }
  };

  return (
    <div className="container">
      <div className="top-bar">
        <button type="button" className="ico-btn back" onClick={() => navigate(-1)}>
          <i className="arrow"></i>
          <span className="visually-hidden">뒤로가기</span>
        </button>
        <h2 className="page-name">{type === "id" ? "아이디 찾기" : "비밀번호 재설정"}</h2>
      </div>

      <div id="find_account" className="page">
        <div className="content-wrap">
          <p className="page-tit">
            {type === "id"
              ? "가입 정보로 가입 이메일을 확인하세요"
              : "이메일 인증 후 새 비밀번호를 설정하세요"}
          </p>
          <ul className="form-list">
            {type === "id" ? (
              <>
                <li className="list-item">
                  <TextInput label="이름" name="name" required isValid
                    value={f.name} onChange={on("name")} placeholder="이름" maxLength={30} />
                </li>
                <li className="list-item">
                  <TextInput label="휴대폰 번호" name="phone" required isValid
                    value={f.phone} onChange={on("phone")} placeholder="'-' 없이 숫자만 입력" maxLength={20} />
                </li>
              </>
            ) : (
              <>
                <li className="list-item">
                  {/* 회원가입과 같은 방식(도메인 선택) — 두 화면의 입력 모양을 맞춘다. */}
                  <EmailField label="이메일" required value={f.email}
                    onChange={(v) => onEmail({ target: { value: v } })} />
                </li>
                <li className="list-item">
                  <button type="button" className="btn-md outlined" onClick={sendCode} disabled={sending}>
                    {sending ? "전송 중…" : codeSent ? "인증번호 재전송" : "인증번호 받기"}
                  </button>
                </li>
                {codeSent && (
                  <>
                    <li className="list-item">
                      <TextInput label="인증번호" name="code" type="text" required isValid
                        value={f.code}
                        onChange={(e) => setF({ ...f, code: e.target.value.replace(/\D/g, "") })}
                        placeholder="6자리 숫자" maxLength={6} />
                    </li>
                    <li className="list-item">
                      <TextInput label="새 비밀번호" name="newPassword" type="password" required isValid
                        value={f.newPassword} onChange={on("newPassword")} placeholder="8자 이상" maxLength={50} />
                    </li>
                    <li className="list-item">
                      <TextInput label="새 비밀번호 확인" name="newPasswordConfirm" type="password" required isValid
                        value={f.newPasswordConfirm} onChange={on("newPasswordConfirm")}
                        placeholder="비밀번호 재입력" maxLength={50} />
                    </li>
                  </>
                )}
                {vmsg && (
                  <li className="list-item">
                    <p className="t-14-single" style={{ color: vmsgErr ? "#e2362d" : "#6D6E71" }}>{vmsg}</p>
                  </li>
                )}
              </>
            )}
          </ul>
        </div>
      </div>

      <div className="tab-bar-btn-group fixed">
        <div className="btn-group">
          <button type="button" disabled={disabled} onClick={submit} className="btn-md filled primary">
            {type === "id" ? "아이디 찾기" : "비밀번호 변경"}
          </button>
        </div>
      </div>

      <div className={`modal-dimmed ${showModal ? "is-show" : ""}`}>
        <div className="modal">
          <div className="tit-box">
            <p style={{ whiteSpace: "pre-line", textAlign: "center" }}>{resultMsg}</p>
          </div>
          <div className="btn-group">
            <button type="button" className="btn-md filled primary"
              onClick={() => { setShowModal(false); navigate("/login"); }}>로그인</button>
            <button type="button" className="btn-md outlined"
              onClick={() => setShowModal(false)}>닫기</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountRecovery;
